import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ref,
  Storage,
  uploadBytesResumable as uploadBytesResumableFn,
  UploadTask,
  UploadTaskSnapshot,
} from '@angular/fire/storage';
import { IVideoFile } from '@feature/customer/requests/video-requests/models/video.interface';
import { VideoRequestService } from '@feature/customer/requests/video-requests/services/video-request.service';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

export interface UploadProgress {
  fileId: string;
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'uploaded' | 'transcoding' | 'saving' | 'completing' | 'completed' | 'error' | 'cancelled';
  error?: string;
  uploadTask?: UploadTask;
  requestId?: number;
  videoPath?: string;
  file?: File;
  folderPath?: string;
  metadata?: {
    name: string;
    size: number;
    type: string;
  };
  videoFiles?: IVideoFile[];
  transcodingJobName?: string;
  transcodingState?: string;
  wasTranscoded?: boolean;
  originalVideoExtension?: string;
}

export interface UploadQueueItem {
  file: File;
  fileId: string;
  folderPath: string;
  timestamp: number;
  requestId?: number;
}

@Injectable()
export class VideoUploadService {
  private readonly _storage = inject(Storage);
  private readonly _videoRequestService = inject(VideoRequestService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _uploadProgressSubjects = new Map<
    string,
    BehaviorSubject<UploadProgress>
  >();
  private readonly _uploadQueue$ = new BehaviorSubject<UploadQueueItem[]>([]);
  private readonly _isUploading$ = new BehaviorSubject<boolean>(false);
  private readonly _completionCallbacks = new Map<
    number,
    (requestId: number, fileCount: number) => void
  >();
  readonly uploadCompleted$ = new BehaviorSubject<number | null>(null);
  readonly allUploadsComplete$ = new BehaviorSubject<boolean>(false);
  readonly uploadsComplete$ = new BehaviorSubject<void>(undefined);
  readonly uploadsInProgress$ = new BehaviorSubject<void>(undefined);

  getAllUploadingFiles$(): Observable<UploadProgress>[] {
    return Array.from(this._uploadProgressSubjects.values()).map(subject =>
      subject.asObservable()
    );
  }

  private checkRequestCompletion(requestId: number): void {
    const filterUploadsByRequestId = Array.from(
      this._uploadProgressSubjects.values()
    )
      .map(subject => subject.value)
      .filter(progress => progress.requestId === requestId);

    if (filterUploadsByRequestId.length === 0) return;

    // Look for files that just finished uploading (status = 'uploaded'), not fully completed
    const uploadedFiles = filterUploadsByRequestId.filter(
      p => p.status === 'uploaded'
    );

    // Start transcoding for EACH uploaded file immediately (don't wait for other uploads)
    // Each video follows its own independent lifecycle
    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach(upload => {
        this.startTranscoding(upload, requestId);
      });
    }
  }

  private startTranscoding(upload: UploadProgress, requestId: number): void {
    const progressSubject = this._uploadProgressSubjects.get(upload.fileId);
    if (!progressSubject) return;

    const fileName = upload.metadata?.name ?? upload.fileName;
    const extension =
      upload.metadata?.type ?? upload.fileName.split('.').pop() ?? '';

    // Update status to transcoding
    progressSubject.next({
      ...progressSubject.value,
      status: 'transcoding',
      transcodingState: 'STARTING',
    });

    this._videoRequestService
      .startTranscode({
        videoRequestId: requestId,
        videoPath: upload.videoPath ?? '',
        fileName: fileName,
        fileExtension: extension,
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: response => {
          if (response.data.transcodingStarted) {
            // Start polling for transcoding status
            progressSubject.next({
              ...progressSubject.value,
              status: 'transcoding',
              transcodingJobName: response.data.jobName,
              transcodingState: 'PROCESSING',
            });
            this.pollTranscodeStatus(
              upload.fileId,
              response.data.jobName,
              requestId
            );
          } else {
            // Transcoding not needed - proceed directly to save
            this.onTranscodingComplete(upload.fileId, requestId, null);
          }
        },
        error: err => {
          console.error('Failed to start transcoding:', err);
          progressSubject.next({
            ...progressSubject.value,
            status: 'error',
            error: 'Failed to start transcoding',
          });
        },
      });
  }

  private pollTranscodeStatus(
    fileId: string,
    jobName: string,
    requestId: number
  ): void {
    const progressSubject = this._uploadProgressSubjects.get(fileId);
    if (!progressSubject) return;

    const pollInterval = setInterval(() => {
      // Stop polling if the upload was cancelled
      if (!this._uploadProgressSubjects.has(fileId)) {
        clearInterval(pollInterval);
        return;
      }

      this._videoRequestService
        .getTranscodeStatus(jobName)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: response => {
            const state = response.data.state?.toUpperCase() ?? '';

            if (state === 'SUCCEEDED') {
              clearInterval(pollInterval);
              progressSubject.next({
                ...progressSubject.value,
                transcodingState: 'SUCCEEDED',
              });
              this.onTranscodingComplete(
                fileId,
                requestId,
                response.data.outputFilePath ?? null
              );
            } else if (state === 'FAILED') {
              clearInterval(pollInterval);
              // Even if transcoding failed, we still save the original video to the database
              // The original file path is preserved and the transcodingState indicates the failure
              progressSubject.next({
                ...progressSubject.value,
                transcodingState: 'FAILED',
                // Keep the original videoPath - do not mark as error
              });
              // Save the original file to the database (passing null means use original path)
              this.onTranscodingComplete(fileId, requestId, null);
            } else if (state === 'RETRYING') {
              // Backend started a retry job (no-audio fallback)
              // outputFilePath contains the new job name
              const newJobName = response.data.outputFilePath;
              if (newJobName) {
                clearInterval(pollInterval);
                progressSubject.next({
                  ...progressSubject.value,
                  transcodingJobName: newJobName,
                  transcodingState: 'PROCESSING (retry)',
                });
                this.pollTranscodeStatus(fileId, newJobName, requestId);
              }
            } else {
              // Still processing
              progressSubject.next({
                ...progressSubject.value,
                transcodingState: state,
              });
            }
          },
          error: () => {
            // Network error during polling - continue polling
            console.warn('Transcode status poll failed, retrying...');
          },
        });
    }, 5000); // Poll every 5 seconds
  }

  private onTranscodingComplete(
    fileId: string,
    requestId: number,
    transcodedPath: string | null
  ): void {
    const progressSubject = this._uploadProgressSubjects.get(fileId);
    if (!progressSubject) return;

    const upload = progressSubject.value;
    // Use the stored original extension (set at upload time) - do not recalculate
    const originalExtension = upload.originalVideoExtension ?? upload.fileName.split('.').pop() ?? '';
    const currentTranscodingState = upload.transcodingState;

    // If transcoding produced an output, update the file path and name
    if (transcodedPath) {
      const transcodedFileName =
        transcodedPath.split('/').pop() ?? upload.fileName;
      progressSubject.next({
        ...progressSubject.value,
        status: 'saving', // Transitional state: transcoding done, waiting for backend API
        videoPath: transcodedPath,
        transcodingState: 'SUCCEEDED',
        wasTranscoded: true,
        // Preserve the original extension - do not overwrite
        originalVideoExtension: originalExtension,
        metadata: {
          ...upload.metadata!,
          name: transcodedFileName,
          // Note: type becomes 'mp4' after transcoding, but originalVideoExtension is preserved
          type: 'mp4',
        },
      });
    } else {
      // Transcoding was skipped, not needed, or failed - save original file
      // Preserve the existing transcodingState (could be 'SKIPPED' or 'FAILED')
      progressSubject.next({
        ...progressSubject.value,
        status: 'saving', // Transitional state: waiting for backend API
        transcodingState: currentTranscodingState === 'FAILED' ? 'FAILED' : 'SKIPPED',
        wasTranscoded: false,
        // Preserve the original extension
        originalVideoExtension: originalExtension,
      });
    }

    // Save THIS video immediately (per-video, not batch)
    this.saveVideoToBackend(fileId, requestId);
  }

  /**
   * Save a single video to the backend immediately after its transcoding completes.
   * Each video is saved independently without waiting for other videos in the batch.
   * 
   * IMPORTANT: Only sends the CURRENT video + existing videos (with real IDs).
   * Does NOT send other session videos (id: 0) to avoid duplicates.
   * Backend has idempotency check as additional safety net.
   */
  private saveVideoToBackend(fileId: string, requestId: number): void {
    const progressSubject = this._uploadProgressSubjects.get(fileId);
    if (!progressSubject) return;

    const upload = progressSubject.value;
    if (upload.status !== 'saving') return;

    // Get existing video files for this request (videos that existed BEFORE this upload session)
    // These have real IDs (> 0) and are safe to include
    const existingVideoFiles = (upload.videoFiles || []).filter(v => v.id && v.id > 0);

    // Create the video file object for THIS video only
    const videoFile: IVideoFile = {
      id: 0,
      video: upload.metadata?.name ?? upload.fileName,
      videoType: 1,
      videoSize: upload.metadata?.size ?? 0,
      videoPath: upload.videoPath ?? '',
      videoExtension:
        upload.metadata?.type ?? upload.fileName.split('.').pop() ?? '',
      wasTranscoded: upload.wasTranscoded ?? false,
      originalVideoExtension: upload.originalVideoExtension,
      transcodeState: upload.transcodingState,
    };

    // Only send: existing videos (with real IDs) + THIS new video
    // Do NOT include other session videos with id: 0 - they handle their own saves
    const allVideoFiles = [...existingVideoFiles, videoFile];

    // Mark as 'completing' to prevent duplicate API calls
    progressSubject.next({
      ...progressSubject.value,
      status: 'completing' as UploadProgress['status'],
    });

    // Call API to save THIS video
    this._videoRequestService
      .addUpdateVideoFiles(requestId, allVideoFiles)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Mark THIS video as completed - show green check immediately
          progressSubject.next({
            ...progressSubject.value,
            status: 'completed',
            progress: 100,
          });
          this.uploadCompleted$.next(requestId);
          this.checkAllUploadsComplete();
        },
        error: err => {
          console.error('Failed to save video file:', err);
          progressSubject.next({
            ...progressSubject.value,
            status: 'error',
            error: 'Failed to save video file',
          });
        },
      });
  }

  /**
   * Legacy method - now only used for batch operations like cancel.
   * Individual video saves are handled by saveVideoToBackend.
   * This method handles edge cases where files need to be saved after cancellation.
   */
  private checkAllTranscodingComplete(requestId: number): void {
    const requestUploads = Array.from(this._uploadProgressSubjects.values())
      .map(s => s.value)
      .filter(p => p.requestId === requestId);

    // Get files that are ready to be saved but haven't been processed yet
    const filesToSave = requestUploads.filter(p => p.status === 'saving');

    // Save each file independently
    filesToSave.forEach(upload => {
      this.saveVideoToBackend(upload.fileId, requestId);
    });
  }

  private checkAllUploadsComplete(): void {
    const allProgresses = Array.from(this._uploadProgressSubjects.values()).map(
      subject => subject.value
    );

    // Check if all uploads are completed (no pending, uploading, or error)
    const allComplete =
      allProgresses.length > 0 &&
      allProgresses.every(p => p.status === 'completed');

    if (allComplete) {
      this.allUploadsComplete$.next(true);
      this.uploadsComplete$.next(undefined);
    }
  }

  queueUploads(
    files: File[],
    folderPath: string,
    autoStart = true,
    requestId?: number,
    videoFiles?: IVideoFile[]
  ): string[] {
    const timestamp = Date.now();
    const fileIds: string[] = [];

    // Clear any previous completed uploads
    if (this._uploadProgressSubjects.size > 0) {
      const allCompleted = Array.from(
        this._uploadProgressSubjects.values()
      ).every(subject => subject.value.status === 'completed');

      if (allCompleted) {
        this.clearCompletedUploads();
      }
    }

    // Reset completion status when new uploads start
    this.allUploadsComplete$.next(false);
    this.uploadsInProgress$.next(undefined);

    files.forEach(file => {
      const fileId = `${timestamp}_${file.name}`;
      fileIds.push(fileId);

      const queueItem: UploadQueueItem = {
        file,
        fileId,
        folderPath,
        timestamp,
        requestId,
      };

      // Add to queue
      const currentQueue = this._uploadQueue$.value;
      this._uploadQueue$.next([...currentQueue, queueItem]);

      // Initialize progress tracking
      const progressSubject = new BehaviorSubject<UploadProgress>({
        fileId,
        fileName: file.name,
        progress: 0,
        status: 'pending',
        requestId,
        videoFiles,
        file,
        folderPath,
      });
      this._uploadProgressSubjects.set(fileId, progressSubject);
    });

    if (autoStart) {
      this.handleUploadQueue();
    }

    return fileIds;
  }

  private handleUploadQueue(): void {
    const queue = this._uploadQueue$.value;

    if (queue.length === 0) {
      this._isUploading$.next(false);
      return;
    }

    if (this._isUploading$.value) {
      return;
    }

    this._isUploading$.next(true);
    from(queue)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .pipe(
        mergeMap(item => {
          return this.uploadFile(item).pipe(
            // Remove item from queue after upload completes (success or error)
            mergeMap(result => {
              const currentQueue = this._uploadQueue$.value;
              this._uploadQueue$.next(
                currentQueue.filter(q => q.fileId !== item.fileId)
              );
              return of(result);
            })
          );
        }, 5) // Parallel uploads: up to 5 videos uploading at the same time
      )
      .subscribe({
        complete: () => {
          this._isUploading$.next(false);
          // Check if new items were added while processing
          if (this._uploadQueue$.value.length > 0) {
            this.handleUploadQueue();
          }
        },
        error: error => {
          this._isUploading$.next(false);
          this._uploadProgressSubjects.forEach(subject => {
            const progress = subject.value;
            if (
              progress.status === 'uploading' ||
              progress.status === 'pending'
            ) {
              subject.next({
                ...progress,
                status: 'error',
                error: error?.message || 'Upload failed',
                progress: 0,
              });
            }
          });

          // Continue processing remaining items even after error
          if (this._uploadQueue$.value.length > 0) {
            this.handleUploadQueue();
          }
        },
      });
  }

  private uploadFile(item: UploadQueueItem): Observable<UploadTaskSnapshot> {
    const { file, fileId, folderPath, timestamp, requestId } = item;
    const filePath = `${folderPath}/${timestamp}_${file.name}`;
    const storage = this._storage;
    const storageRef = ref(storage, filePath);

    const progressSubject = this._uploadProgressSubjects.get(fileId);

    if (!progressSubject) {
      return of({} as UploadTaskSnapshot);
    }

    return new Observable<UploadTaskSnapshot>(observer => {
      let isSubscriptionActive = true;
      let uploadTask: UploadTask | null = null;

      try {
        uploadTask = uploadBytesResumableFn(storageRef, file);

        progressSubject.next({
          ...progressSubject.value,
          status: 'uploading',
          uploadTask,
        });

        uploadTask.on(
          'state_changed',
          snapshot => {
            try {
              if (!isSubscriptionActive) return;

              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              progressSubject.next({
                ...progressSubject.value,
                progress,
                status: 'uploading',
              });
            } catch (err) {
              console.error('Error in state_changed:', err);
            }
          },
          error => {
            try {
              isSubscriptionActive = false;
              if (uploadTask) {
                uploadTask.cancel();
              }

              if (progressSubject.value.status !== 'cancelled') {
                const errorMessage =
                  error?.message || error?.code || 'Connection error';

                progressSubject.next({
                  ...progressSubject.value,
                  status: 'error',
                  error: errorMessage,
                  progress: 0,
                  uploadTask: undefined,
                });

                observer.error(error);
              }
            } catch (err) {
              console.error('Error in error handler:', err);
              observer.error(err);
            }
          },
          () => {
            try {
              if (!isSubscriptionActive) return;

              const snapshot = uploadTask?.snapshot;
              if (!snapshot) {
                observer.error(new Error('Upload snapshot not available'));
                return;
              }

              progressSubject.next({
                ...progressSubject.value,
                status: 'uploaded',
                progress: 100,
                videoPath: filePath,
                // Store the original extension at upload time - this is immutable
                originalVideoExtension:
                  snapshot.metadata.contentType?.split('/')[1] ??
                  file.name.split('.').pop() ??
                  '',
                metadata: {
                  name: snapshot.metadata.name ?? file.name,
                  size: snapshot.metadata.size ?? file.size,
                  type:
                    snapshot.metadata.contentType?.split('/')[1] ??
                    file.name.split('.').pop() ??
                    '',
                },
              });
              observer.next(snapshot);
              observer.complete();

              // Check if all uploads for this request are complete
              if (requestId !== undefined) {
                this.checkRequestCompletion(requestId);
              }
            } catch (err) {
              console.error('Error in completion handler:', err);
              observer.error(err);
            }
          }
        );
      } catch (error) {
        console.error('Error creating upload task:', error);

        progressSubject.next({
          ...progressSubject.value,
          status: 'error',
          error: 'Failed to start upload',
          progress: 0,
          uploadTask: undefined,
        });

        observer.error(error);
      }
      return () => {
        isSubscriptionActive = false;
        if (uploadTask && progressSubject.value.status === 'uploading') {
          try {
            uploadTask.cancel();
          } catch (err) {
            console.error('Error cancelling upload task:', err);
          }
        }
      };
    });
  }

  cancelUpload(fileId: string): void {
    const progressSubject = this._uploadProgressSubjects.get(fileId);

    if (progressSubject) {
      const progress = progressSubject.value;
      const requestId = progress.requestId;

      progressSubject.next({
        ...progress,
        status: 'cancelled',
        progress: 0,
        uploadTask: undefined,
      });

      if (
        progress.uploadTask &&
        (progress.status === 'uploading' || progress.status === 'pending')
      ) {
        progress.uploadTask.cancel();
      }

      const currentQueue = this._uploadQueue$.value;
      this._uploadQueue$.next(
        currentQueue.filter(item => item.fileId !== fileId)
      );
      progressSubject.complete();
      this._uploadProgressSubjects.delete(fileId);

      if (requestId !== undefined) {
        this.checkRequestCompletion(requestId);
      }
    }
  }

  clearCompletedUploads(): void {
    this._uploadProgressSubjects.forEach(subject => {
      subject.complete();
    });
    this._uploadProgressSubjects.clear();
    this.allUploadsComplete$.next(false);
  }

  cancelAllUploads(): void {
    this._uploadProgressSubjects.forEach((subject, _fileId) => {
      const progress = subject.value;

      if (
        progress.uploadTask &&
        (progress.status === 'uploading' || progress.status === 'pending')
      ) {
        progress.uploadTask.cancel();
      }
      subject.complete();
    });

    this._uploadProgressSubjects.clear();
    this._uploadQueue$.next([]);
    this._isUploading$.next(false);
  }

  pauseAllUploads(): void {
    this._uploadProgressSubjects.forEach(subject => {
      const progress = subject.value;

      if (
        progress.uploadTask &&
        (progress.status === 'uploading' || progress.status === 'pending')
      ) {
        try {
          progress.uploadTask.cancel();
        } catch (err) {
          console.error(`Error stopping upload ${progress.fileName}:`, err);
        }

        subject.next({
          ...progress,
          status: 'error',
          error: 'Connection lost - upload paused',
          progress: 0,
          uploadTask: undefined,
        });
      }
    });
    this._uploadQueue$.next([]);
    this._isUploading$.next(false);
  }

  retryUpload(fileId: string): void {
    const progressSubject = this._uploadProgressSubjects.get(fileId);
    if (!progressSubject) return;

    const progress = progressSubject.value;

    if (progress.status !== 'error' || !progress.file) return;
    progressSubject.next({
      ...progress,
      status: 'pending',
      progress: 0,
      error: undefined,
      uploadTask: undefined,
    });

    const newTimestamp = Date.now();
    const queueItem: UploadQueueItem = {
      file: progress.file,
      fileId: progress.fileId,
      folderPath: progress.folderPath ?? '',
      timestamp: newTimestamp,
      requestId: progress.requestId,
    };

    const currentQueue = this._uploadQueue$.value;
    this._uploadQueue$.next([...currentQueue, queueItem]);

    if (!this._isUploading$.value) {
      this.handleUploadQueue();
    }
  }
}
